<template>
  <div v-if="fileMetadata.length" class="details w-full">
    <div class="flex mb-[10px] mt-[34px] font-600">
      <div>
        {{ t('canvasUI.browserApp.fileSelectionInfo.sections.details.metadata') }}
      </div>
    </div>

    <div class="flex flex-wrap">
      <div v-for="(item, index) in fileMetadata" :key="index" class="flex flex-col gap-2 metadata-field mb-4">
        <div class="title">
          {{ item.label }}
        </div>
        <div>
          {{ item.value }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, onMounted, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { MetadataTemplateFieldTypeEnum } from '@openapi'
import useThemeVars from '@lib/composables/useThemeVars'
import type { GetInstanceMetadataTemplatesPayload } from '@sdk/payload.types'
import type {
  PaginatedMetadataTemplateList,
  MetadataTemplate,
  File,
  MetadataTemplateSection,
  MetadataTemplateField,
} from '@openapi'

const cFilePanelProps = inject<{ file?: Ref<File> }>('CFilePanelProps')
const metadataTemplateFetcher = inject<
  ((params?: GetInstanceMetadataTemplatesPayload) => Promise<PaginatedMetadataTemplateList>) | undefined
>('instanceMetadataTemplatesFetcher')
const { t } = useI18n()
const themeVars = useThemeVars()
const metadataTemplate = ref<MetadataTemplate | null>()
const file = computed(() => cFilePanelProps?.file?.value || null)
const fileMetadata = computed(() => {
  if (!metadataTemplate.value || !file.value?.metadata) return []

  const metadata = []
  const sectionFieldsDict = metadataTemplate.value.sections?.reduce(
    (dict, section: MetadataTemplateSection) => {
      section.fields.forEach((field: MetadataTemplateField) => {
        dict[field.name] = {
          ...field,
          optionsDict: field.options?.reduce(
            (acc, option) => ({
              ...acc,
              [option.value]: option.label,
            }),
            {} as Record<string, string>,
          ),
        }
      })
      return dict
    },
    {} as Record<string, MetadataTemplateField & { optionsDict?: Record<string, string> }>,
  )

  if (!sectionFieldsDict) return []

  for (const [id, value] of Object.entries(file.value.metadata)) {
    const field = sectionFieldsDict[id]
    if (field === undefined) continue
    let metadataValue = value

    if (field.type === MetadataTemplateFieldTypeEnum.SELECT) metadataValue = field.optionsDict?.[value]
    else if (field.type === MetadataTemplateFieldTypeEnum.MULTI_SELECT)
      metadataValue = value?.map ? value.map((id: string) => field.optionsDict?.[id]).join(', ') : metadataValue

    metadata.push({
      label: sectionFieldsDict[id].label,
      value:
        metadataValue === true
          ? t('canvasUI.common.yes')
          : metadataValue === false
            ? t('canvasUI.common.no')
            : metadataValue || '-',
    })
  }

  return metadata
})

onMounted(async () => {
  const t = (await metadataTemplateFetcher?.())?.results?.[0]
  if (t) metadataTemplate.value = t
})
</script>

<style lang="scss" scoped>
.metadata-field {
  flex: 1 1 50%;
}
.title {
  color: v-bind('themeVars.text2');
  font-style: normal;
  font-size: 15px;
  line-height: 18px;
  letter-spacing: 0.15px;
}

.not-available {
  color: v-bind('themeVars.text3');
}
</style>
